import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlass, ArrowRight } from '@phosphor-icons/react';
import { useSEO } from '@/hooks/useSEO';
import PublicNav from '@/components/layout/PublicNav';
import PublicFooter from '@/components/layout/PublicFooter';
import { DOCS_CATEGORIES, searchArticles } from './docsData';

export default function DocsIndex() {
  const [query, setQuery] = useState('');
  const results = searchArticles(query);

  useSEO({
    title: 'Documentation — Admitrix',
    description: 'Guides, tutorials, and documentation for the Admitrix admissions CRM. Learn how to manage leads, pipeline, fees, communication, and automation.',
  });

  return (
    <div className="bg-background text-foreground min-h-screen">
      <PublicNav />

      <main className="pt-28 pb-20 px-6 lg:px-12 max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-[10px] font-mono uppercase tracking-[0.15em] mb-3" style={{ color: '#E04B2A' }}>DOCUMENTATION</p>
          <h1 className="font-display text-4xl font-extrabold text-foreground tracking-tight leading-[1.1]" data-testid="docs-title">Learn Admitrix</h1>
          <p className="text-base text-muted-foreground mt-3 max-w-lg mx-auto">Guides, tutorials, and references for every feature. From first login to advanced automation.</p>
        </div>

        {/* Search */}
        <div className="max-w-md mx-auto mb-12 relative">
          <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search docs..."
            className="w-full h-10 pl-9 pr-4 rounded-lg border border-border bg-card text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border"
            data-testid="docs-search"
          />
          {query.length >= 2 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto z-10" data-testid="docs-search-results">
              {results.length === 0 ? (
                <p className="px-4 py-3 text-[12px] text-muted-foreground">No results found</p>
              ) : (
                results.map(r => (
                  <Link
                    key={`${r.categoryId}-${r.slug}`}
                    to={`/docs/${r.categoryId}/${r.slug}`}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-secondary transition-colors"
                    onClick={() => setQuery('')}
                  >
                    <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-bold" style={{ color: r.categoryColor, backgroundColor: `${r.categoryColor}12` }}>
                      {r.categoryTitle.split(' ')[0]}
                    </span>
                    <span className="text-[12px] text-foreground">{r.title}</span>
                  </Link>
                ))
              )}
            </div>
          )}
        </div>

        {/* Category cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="docs-categories">
          {DOCS_CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const firstSlug = cat.articles[0]?.slug || 'overview';
            return (
              <Link
                key={cat.id}
                to={`/docs/${cat.id}/${firstSlug}`}
                className="group p-5 rounded-xl border border-border bg-card hover:border-foreground/10 transition-all duration-300"
                data-testid={`docs-cat-card-${cat.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110" style={{ backgroundColor: `${cat.color}12`, color: cat.color }}>
                    <Icon size={18} weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-display text-sm font-bold text-foreground mb-1 group-hover:underline">{cat.title}</h2>
                    <p className="text-[12px] text-muted-foreground leading-relaxed mb-3">{cat.description}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {cat.articles.length} {cat.articles.length === 1 ? 'article' : 'articles'}
                    </p>
                  </div>
                  <ArrowRight size={14} weight="bold" className="text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0 mt-1" />
                </div>
              </Link>
            );
          })}
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
